import {
	WebGLRenderer,
	Group,
	Scene,
	Color,
	SRGBColorSpace,
	ACESFilmicToneMapping,
	EquirectangularReflectionMapping,
} from 'three';
import { CameraController } from './controls/CameraController';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Loader } from './utils/Loader';
import { GUI } from 'dat.gui';
import { Mouse } from './input/Mouse';
import { Keyboard } from './input/Keyboard';
import { CameraType, CreatorStep } from '@/types/creator';
import { EventEmitter } from './utils/EventEmitter';
import {
	RendererMode,
	SerializedRenderer,
	ThreeEvent,
	ThreeEvents,
} from './types';
import { Exporter, ExportType } from './utils/Exporter';
import { LightController } from './controls/LightsController';
import { RoomManager } from './managers/RoomManager';
import { ProjectPlane } from './objects/ProjectPlane';
import {
	ModulesManager,
	SerializedModulesManager,
} from './managers/ModulesManager';
import { ModulesGrid } from './utils/ModulesGrid';
import { formatExactDate } from '@/utils/utilities';
import { MeasurementManager } from './managers/MeasurementManager';
import { DrawingManager } from './managers/DrawingManager';

import RENDERER_CONFIG from '@/configs/rendererConfig';
import envMapPath from '@/assets/hdri/studio.exr';
import APP_CONFIG from '@/configs/appConfig';

import Stats from 'three/examples/jsm/libs/stats.module.js';
import html2canvas from 'html2canvas';

const stats = new Stats();
// document.body.appendChild(stats.dom);

export class ProjectRenderer {
	private static instance: ProjectRenderer = new ProjectRenderer();
	private loader = Loader.getInstance();
	private exporter = new Exporter(this);
	private _mode = RendererMode.ROOM;
	private css2DRenderer!: CSS2DRenderer;
	private screenshotCss2DRenderer!: CSS2DRenderer;
	private _currentLevel = 0;
	isDev = import.meta.env.DEV;
	renderer!: WebGLRenderer;
	screenshotRenderer!: WebGLRenderer;
	private lightsController!: LightController;
	measurementManager = new MeasurementManager(this);
	cameraController!: CameraController;
	room = new Group();
	mouse = new Mouse(this);
	keyboard = new Keyboard(this);
	roomManager = new RoomManager(this);
	modulesManager = new ModulesManager(this);
	drawingManager = new DrawingManager(this);
	emitter = new EventEmitter<ThreeEvents>();
	loading = false;
	projectPlane = new ProjectPlane(this);
	GUI!: GUI;
	scene!: Scene;
	canvas: HTMLCanvasElement | null = null;

	private constructor() {}

	static getInstance() {
		return ProjectRenderer.instance;
	}

	get currentLevel() {
		return this._currentLevel;
	}

	set currentLevel(value: number) {
		this._currentLevel = value;
		const levelY = value * ModulesGrid.BLOCK_HEIGHT;

		this.mouse.plane.constant = -levelY;
		this.modulesManager.setModulesLevelVisibility();
	}

	get mode() {
		return this._mode;
	}

	set mode(value: RendererMode) {
		this._mode = value;
		this.setAvailability();

		switch (value) {
			default:
				break;
		}
	}

	private initGUI() {
		this.GUI = new GUI();
	}

	private initRenderers() {
		this.renderer = new WebGLRenderer({
			canvas: this.canvas!,
			antialias: true,
		});

		this.scene.background = new Color(RENDERER_CONFIG.COLORS.LIGHT_CREAM);

		const offscreenCanvas = new OffscreenCanvas(
			RENDERER_CONFIG.SCREENSHOT.WIDTH,
			RENDERER_CONFIG.SCREENSHOT.HEIGHT
		);

		this.screenshotRenderer = new WebGLRenderer({
			canvas: offscreenCanvas,
			antialias: true,
			preserveDrawingBuffer: true,
		});

		for (const renderer of [this.renderer, this.screenshotRenderer]) {
			renderer.outputColorSpace = SRGBColorSpace;
			renderer.toneMapping = ACESFilmicToneMapping;
			renderer.toneMappingExposure = 1;
		}

		this.css2DRenderer = new CSS2DRenderer();
		this.screenshotCss2DRenderer = new CSS2DRenderer();

		for (const css2dRenderer of [
			this.css2DRenderer,
			this.screenshotCss2DRenderer,
		]) {
			css2dRenderer.domElement.style.pointerEvents = 'none';
			this.canvas!.parentElement!.appendChild(css2dRenderer.domElement);
		}

		this.css2DRenderer.domElement.style.zIndex =
			APP_CONFIG.Z_INDEX.CSS_2D_RENDERER.toString();
		this.css2DRenderer.domElement.style.position = 'absolute';
		this.css2DRenderer.domElement.style.top = '0px';

		this.screenshotCss2DRenderer.domElement.style.background = 'none';
		this.screenshotCss2DRenderer.domElement.style.position = 'fixed';
		this.screenshotCss2DRenderer.domElement.style.zIndex = '-1';

		this.screenshotCss2DRenderer.setSize(
			RENDERER_CONFIG.SCREENSHOT.WIDTH,
			RENDERER_CONFIG.SCREENSHOT.HEIGHT
		);

		this.renderer.setAnimationLoop(() => {
			stats.begin();

			switch (this.cameraController.activeCamera) {
				case CameraType.ORTHOGRAPHIC:
					this.cameraController.orthographicControls.update();
					this.css2DRenderer.render(
						this.scene,
						this.cameraController.orthographicCamera
					);
					this.renderer.render(
						this.scene,
						this.cameraController.orthographicCamera
					);
					break;
				case CameraType.PERSPECTIVE:
					this.cameraController.perspectiveControls.update();
					this.css2DRenderer.render(
						this.scene,
						this.cameraController.perspectiveCamera
					);
					this.renderer.render(
						this.scene,
						this.cameraController.perspectiveCamera
					);
					break;

				default:
					throw new Error('UNKNOWN_CAMERA_TYPE');
			}

			stats.end();
		});
	}

	private loadEnvMap() {
		this.loader.eXRLoader.load(envMapPath, (texture) => {
			texture.mapping = EquirectangularReflectionMapping;

			this.scene.environment = texture;
			this.scene.environmentIntensity = 0.4;
		});
	}

	init(canvas: HTMLCanvasElement) {
		this.canvas = canvas;

		if (import.meta.env.DEV) this.initGUI();

		this.scene = new Scene();

		this.scene.add(this.room);

		this.cameraController = new CameraController(this);
		this.lightsController = new LightController(this);

		this.mouse.init();
		this.keyboard.init();

		this.projectPlane.createPlane();
		this.modulesManager.modulesGrid.init();
		this.loadEnvMap();
		this.initRenderers();

		this.handleResize();
	}

	unHoverAllObjects() {
		this.roomManager.unHoverAll();
		this.modulesManager.unHoverAll();
		this.emitter.emit(ThreeEvent.MOUSE_OVER_MODULE, null);
	}

	handleCreatorStepChange(step: CreatorStep) {
		switch (step) {
			case CreatorStep.SHAPES:
				this.mode = RendererMode.ROOM;
				break;
			case CreatorStep.MODULES:
				this.mode = RendererMode.MODULES;
				break;
			case CreatorStep.CONTACT:
				this.mode = RendererMode.PREVIEW;
				break;
		}
	}

	setAvailability() {
		this.mouse.setAvailability();
		this.roomManager.setAvailability();
		this.modulesManager.setAvailability();
		this.measurementManager.setAvailability();
		this.mouse.cursorHandler.setCursorType();
	}

	async screenshot() {
		this.roomManager.prepareScreenshot();
		this.cameraController.prepareScreenshotCameras();

		this.screenshotRenderer.render(
			this.scene,
			this.cameraController.screenshotOrthographicCamera
		);
		this.screenshotCss2DRenderer.render(
			this.scene,
			this.cameraController.screenshotOrthographicCamera
		);

		const files: File[] = [];

		let canvas = this.screenshotRenderer
			.domElement as unknown as OffscreenCanvas;

		const finalCanvas = new OffscreenCanvas(canvas.width, canvas.height);
		const css2dCanvas = await html2canvas(
			this.screenshotCss2DRenderer.domElement,
			{
				logging: false,
				backgroundColor: null,
			}
		);

		const ctx = finalCanvas.getContext('2d');
		ctx!.drawImage(canvas, 0, 0);
		ctx!.drawImage(css2dCanvas, 0, 0);

		let blob = await finalCanvas.convertToBlob();
		const orthoImageFile = new File(
			[blob],
			`Konfiguracja projektu ${formatExactDate()} rzut prostopadły.png`
		);

		files.push(orthoImageFile);

		for (
			let index = 0;
			index < this.cameraController.screenshotPerspectiveCameras.length;
			index++
		) {
			const camera = this.cameraController.screenshotPerspectiveCameras[index];

			this.screenshotRenderer.render(this.scene, camera);
			canvas = this.screenshotRenderer.domElement as unknown as OffscreenCanvas;
			blob = await canvas.convertToBlob();
			const file = new File(
				[blob],
				`Konfiguracja projektu ${formatExactDate()} - ujęcie ${index + 1}.png`
			);

			files.push(file);
		}

		this.roomManager.prepareScreenshot(true);

		return files;
	}

	handleCameraCenter() {
		this.cameraController.centerCamera();
	}

	handleCameraZoom(value: number) {
		this.cameraController.handleCameraZoom(value);
	}

	handleResize() {
		const { width, height } =
			this.canvas!.parentElement!.getBoundingClientRect();

		this.cameraController.handleResize(width / height);

		this.renderer.setSize(width, height);
		this.css2DRenderer.setSize(width, height);
	}

	dispose() {
		this.renderer.dispose();
		this.cameraController.dispose();

		const ctx = this.canvas!.getContext('webgl2')!;
		ctx.clear(RENDERER_CONFIG.SCENE_BACKGROUND);
	}

	export(): SerializedRenderer {
		return {
			roomManger: this.roomManager.export(),
			modulesManger: this.modulesManager.export(),
			measurementManager: this.measurementManager.export(),
		};
	}

	async loadFromProject(data: SerializedRenderer) {
		this.modulesManager.historyManager.cancelGroupTransaction();
		this.mouse.cancelActiveInteractions();
		this.modulesManager.clearAllModules();

		this.roomManager.loadFromData(data.roomManger);
		await this.modulesManager.loadFromData(data.modulesManger, {
			skipHistory: true,
		});
		this.measurementManager.loadFromData(data.measurementManager);
		this.modulesManager.historyManager.reset();
	}

	async loadFromPreset(data: SerializedModulesManager) {
		const before = this.modulesManager.historyManager.capture();

		this.modulesManager.clearProductModules();
		this.modulesManager.modulesGrid.clear();
		await this.modulesManager.loadFromData(data, { skipHistory: true });

		const productModules = Array.from(
			this.modulesManager.productModules.values()
		);

		if (!productModules.length) {
			this.modulesManager.historyManager.commit(before);
			return;
		}

		this.modulesManager.historyManager.beginGroupTransaction(before);
		this.mouse.setDragginGroup(productModules);
	}

	async exportProjectModel(type: ExportType) {
		return await this.exporter.exportProject(type);
	}
}
